/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
// Admin API client for the AI Website Guardian and AI Support Copilot.
// Every decision (risk class, whether something may run, quotas) is made by the backend — this file only transports.
import axios from "axios";
import { API } from "@/lib/api";

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

/** The message to show an administrator for a failed call — the backend's own explanation when it gave one. */
export function aiErrorMessage(err, fallback = "Something went wrong. Nothing was changed.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (err?.response?.status === 401 || err?.response?.status === 403) return "Your admin session has expired. Sign in again.";
  return fallback;
}

export async function fetchAiStatus(token) {
  return (await axios.get(`${API}/admin/ai/status`, auth(token))).data;
}

export async function fetchAiSettings(token) {
  return (await axios.get(`${API}/admin/ai/settings`, auth(token))).data;
}

export async function updateAiSettings(token, patch) {
  return (await axios.patch(`${API}/admin/ai/settings`, patch, auth(token))).data;
}

export async function fetchAiAudit(token, limit = 10) {
  return (await axios.get(`${API}/admin/ai/audit`, { ...auth(token), params: { limit } })).data;
}

// ---- Guardian -------------------------------------------------------------------------------------------------
export async function analyzeIncidentWithAi(token, incidentId) {
  return (await axios.post(`${API}/admin/ai/guardian/incidents/${incidentId}/analyze`, {}, auth(token))).data;
}

export async function fetchIncidentAi(token, incidentId) {
  return (await axios.get(`${API}/admin/ai/guardian/incidents/${incidentId}`, auth(token))).data;
}

export async function fetchIncidentAiEvidence(token, incidentId) {
  return (await axios.get(`${API}/admin/ai/guardian/incidents/${incidentId}/evidence`, auth(token))).data;
}

export async function fetchGuardianRepairs(token, status) {
  return (await axios.get(`${API}/admin/ai/guardian/repairs`, { ...auth(token), params: status ? { status } : {} })).data;
}

export async function approveGuardianRepair(token, repairId, note) {
  return (await axios.post(`${API}/admin/ai/guardian/repairs/${repairId}/approve`, note ? { note } : {}, auth(token))).data;
}

export async function rejectGuardianRepair(token, repairId, note) {
  return (await axios.post(`${API}/admin/ai/guardian/repairs/${repairId}/reject`, note ? { note } : {}, auth(token))).data;
}

export async function verifyGuardianRepair(token, repairId) {
  return (await axios.post(`${API}/admin/ai/guardian/repairs/${repairId}/verify`, {}, auth(token))).data;
}

export async function resolveGuardianRepair(token, repairId, note) {
  return (await axios.post(`${API}/admin/ai/guardian/repairs/${repairId}/resolve`, note ? { note } : {}, auth(token))).data;
}

// ---- Support Copilot ------------------------------------------------------------------------------------------
export async function fetchSupportTickets(token) {
  return (await axios.get(`${API}/admin/support/tickets`, auth(token))).data;
}

export async function fetchSupportDrafts(token, ticketId) {
  return (await axios.get(`${API}/admin/ai/support/tickets/${ticketId}/drafts`, auth(token))).data;
}

export async function generateSupportDraft(token, ticketId, { instructions, regenerate } = {}) {
  return (await axios.post(`${API}/admin/ai/support/tickets/${ticketId}/draft`, { instructions: instructions || undefined, regenerate: !!regenerate }, auth(token))).data;
}

export async function editSupportDraft(token, draftId, reply) {
  return (await axios.patch(`${API}/admin/ai/support/drafts/${draftId}`, { reply }, auth(token))).data;
}

export async function sendSupportDraft(token, draftId, acknowledgeWarnings = false) {
  return (await axios.post(`${API}/admin/ai/support/drafts/${draftId}/send`, { acknowledge_warnings: !!acknowledgeWarnings }, auth(token))).data;
}

export async function escalateSupportDraft(token, draftId, note) {
  return (await axios.post(`${API}/admin/ai/support/drafts/${draftId}/escalate`, note ? { note } : {}, auth(token))).data;
}

export async function askGuestAiHelp(payload) {
  return (await axios.post(`${API}/ai/help/guest/chat`, payload)).data;
}

export async function askCustomerAiHelp(token, payload) {
  return (await axios.post(`${API}/ai/help/customer/chat`, payload, auth(token))).data;
}
